import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Typography, typographyVariants } from 'components/ui/typography';
import {
  Currency,
  ProductsSortByValue,
  ProductsSortInput,
  SortDirectionValue,
} from 'app/graphql/generated/admin/graphql';
import { env } from 'config/env';
import { useFlags } from 'providers/FlagsProvider';

import { FacetChangeValue, Facets } from './components/Facets';
import { ProductList } from './components/ProductList';
import { CatalogProduct, ProductDetail } from './components/ProductDetail';

const PRODUCTION_CATALOG_URL = 'https://console.rye.com/catalog';

// "Default" is a UX affordance for clearing the sort, not a real ranking
// the API surfaces — its `sort` is undefined and the query falls through
// to whatever order the catalog endpoint returns when no sort is given.
const SORT_OPTIONS: Array<{ value: string; label: string; sort?: ProductsSortInput }> = [
  { value: 'default', label: 'Default' },
  {
    value: 'price-asc',
    label: 'Price: Low to High',
    sort: { sortBy: ProductsSortByValue.Price, direction: SortDirectionValue.Asc },
  },
  {
    value: 'price-desc',
    label: 'Price: High to Low',
    sort: { sortBy: ProductsSortByValue.Price, direction: SortDirectionValue.Desc },
  },
];

const Page = () => {
  const [storeDomains, setStoreDomains] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState<number>();
  const [maxPrice, setMaxPrice] = useState<number>();
  const [sortKey, setSortKey] = useState<string>('default');
  const [currencies, setCurrencies] = useState<Currency[]>([Currency.Usd]);
  const [selectedProduct, setSelectedProduct] = useState<CatalogProduct | null>(null);

  // The catalog isn't populated in staging, so non-admins (who lack the
  // user-impersonation feature) have no useful data to browse there. Send
  // them to production rather than loading an empty catalog.
  const { ryeAdminAccount: isAdminUser } = useFlags();
  if (env.VITE_NODE_ENV === 'staging' && !isAdminUser) {
    return (
      <div className="flex flex-1 items-center justify-center bg-[var(--bg)] px-8 py-16">
        <div className="max-w-[48ch] text-center">
          <Typography variant="h2" as="h1">
            Catalog unavailable in staging
          </Typography>
          <Typography variant="small" as="p" className="mt-4 text-[var(--text-muted)]">
            Rye's product catalog is only available in our production environment.{' '}
            <a
              href={PRODUCTION_CATALOG_URL}
              target="_blank"
              rel="noreferrer"
              className={typographyVariants({ variant: 'link' })}
            >
              Switch to production
            </a>{' '}
            to browse products in Rye's network.
          </Typography>
        </div>
      </div>
    );
  }

  const sortOption = SORT_OPTIONS.find((o) => o.value === sortKey) ?? SORT_OPTIONS[0];
  // Sort options are price-based and only meaningful when a single
  // currency is selected — disable the dropdown otherwise (and reset
  // to default) so we don't fire a query that mixes currencies.
  const sortDisabled = currencies.length !== 1;

  const handleFacetChange = (value: FacetChangeValue) => {
    if (value.type === 'store') {
      setStoreDomains((prev) => {
        if (value.isChecked) {
          return [...prev, value.value];
        }
        return prev.filter((domain) => domain !== value.value);
      });
    } else if (value.type === 'price') {
      setMinPrice(value.minPrice);
      setMaxPrice(value.maxPrice);
    } else if (value.type === 'currency') {
      setCurrencies((prev) => {
        const next = value.isChecked
          ? [...prev, value.value]
          : prev.filter((currency) => currency !== value.value);
        // If multi-currency selection invalidates the active sort,
        // collapse back to default to keep the query coherent.
        if (next.length !== 1 && sortKey !== 'default') {
          setSortKey('default');
        }
        return next;
      });
    }
  };

  const handleReset = (value: { type: 'store' | 'currency' }) => {
    if (value.type === 'store') {
      setStoreDomains([]);
    } else if (value.type === 'currency') {
      setCurrencies([Currency.Usd]);
    }
  };

  return (
    // Two-pane body. The filter rail is `position: sticky` against the
    // sticky topnav using --chrome-height (set from a ResizeObserver in
    // Layout), so it pins flush to the chrome without a hardcoded
    // offset. items-start lets sticky work; align-items:stretch (the
    // flex default) would override it.
    <div className="flex flex-1 items-start">
      <aside className="sticky top-[var(--chrome-height,111px)] flex h-[calc(100vh_-_var(--chrome-height,111px))] w-[260px] flex-shrink-0 flex-col overflow-y-auto border-r border-[var(--line)] bg-[var(--surface)]">
        <Facets
          onFacetChange={handleFacetChange}
          storeDomains={storeDomains}
          currencies={currencies}
          showReset={{
            store: storeDomains.length > 0,
            currency: currencies.length > 1,
          }}
          onReset={handleReset}
        />
      </aside>
      <div className="min-w-0 flex-1 bg-[var(--bg)]">
        <div className="px-8 py-8">
          <div className="mb-6 flex items-end justify-between gap-6">
            <div>
              <Typography variant="h2" as="h1">
                Products in Rye's network
              </Typography>
              <Typography variant="small" as="p" className="mt-1 max-w-[60ch]">
                Browse Rye's universal product catalog. Filter by store, price, or currency.
              </Typography>
              {storeDomains.length > 0 && (
                <div className="mt-3">
                  <Badge variant="neutral">{storeDomains.length} stores selected</Badge>
                </div>
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={sortDisabled} className="gap-2">
                  <Typography variant="eyebrow" as="span">
                    Sort
                  </Typography>
                  <Typography variant="small" as="span" className="text-[var(--text)]">
                    {sortOption.label}
                  </Typography>
                  <ChevronDown className="h-3.5 w-3.5 text-[var(--text-muted)]" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuRadioGroup value={sortKey} onValueChange={setSortKey}>
                  {SORT_OPTIONS.map((opt) => (
                    <DropdownMenuRadioItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <ProductList
            storeDomains={storeDomains}
            sortBy={sortOption.sort}
            minPrice={minPrice}
            maxPrice={maxPrice}
            currencies={currencies}
            onProductClick={setSelectedProduct}
          />
        </div>
      </div>
      <ProductDetail
        product={selectedProduct}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedProduct(null);
          }
        }}
      />
    </div>
  );
};

export default Page;
